"use client";

import { cn } from "cn";
import type * as React from "react";

const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const monthLabelFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

const dayLabelFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});

function monthIndex(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

interface CalendarNavButtonProps extends React.ComponentProps<"button"> {
  direction: "previous" | "next";
}

function CalendarNavButton({
  direction,
  className,
  ...props
}: CalendarNavButtonProps) {
  return (
    <button
      type="button"
      data-slot="calendar-nav-button"
      aria-label={`Go to ${direction} month`}
      className={cn(
        "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm",
        "bg-transparent text-foreground outline-none transition-colors",
        "hover:bg-accent active:bg-accent",
        "focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {direction === "previous" ? (
          <path d="m15 18-6-6 6-6" />
        ) : (
          <path d="m9 18 6-6-6-6" />
        )}
      </svg>
    </button>
  );
}

type DateRange = { from: Date; to: Date | null };

type RangePosition = "start" | "middle" | "end";

type CalendarBaseProps = {
  month: Date;
  onMonthChange: (month: Date) => void;
  minDate?: Date;
  minMonth?: Date;
  maxMonth?: Date;
  className?: string;
};

type CalendarSingleProps = CalendarBaseProps & {
  mode?: "single";
  value?: Date | null;
  onSelect: (date: Date) => void;
};

type CalendarRangeProps = CalendarBaseProps & {
  mode: "range";
  value?: DateRange | null;
  onSelect: (range: DateRange) => void;
};

type CalendarProps = CalendarSingleProps | CalendarRangeProps;

/** The range after clicking `date`: a new start, or the end of an open range. */
function nextRange(range: DateRange | null | undefined, date: Date): DateRange {
  if (!range || range.to !== null) return { from: date, to: null };
  return date.getTime() < startOfDay(range.from).getTime()
    ? { from: date, to: range.from }
    : { from: range.from, to: date };
}

function rangePosition(
  date: Date,
  range: DateRange | null | undefined,
): RangePosition | null {
  if (!range) return null;
  if (isSameDay(date, range.from)) return "start";
  if (range.to === null) return null;
  if (isSameDay(date, range.to)) return "end";
  const time = date.getTime();
  return time > startOfDay(range.from).getTime() &&
    time < startOfDay(range.to).getTime()
    ? "middle"
    : null;
}

function Calendar(props: CalendarProps) {
  const { month, onMonthChange, minDate, minMonth, maxMonth, className } =
    props;
  const year = month.getFullYear();
  const monthOfYear = month.getMonth();
  const daysInMonth = new Date(year, monthOfYear + 1, 0).getDate();
  const firstWeekday = (new Date(year, monthOfYear, 1).getDay() + 6) % 7;
  const today = startOfDay(new Date());
  const minSelectable = minDate ? startOfDay(minDate) : null;

  const previousDisabled =
    minMonth !== undefined && monthIndex(month) <= monthIndex(minMonth);
  const nextDisabled =
    maxMonth !== undefined && monthIndex(month) >= monthIndex(maxMonth);

  function selectDay(date: Date) {
    if (props.mode === "range") props.onSelect(nextRange(props.value, date));
    else props.onSelect(date);
  }

  return (
    <div
      data-slot="calendar"
      className={cn("flex w-fit flex-col gap-2", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <CalendarNavButton
          direction="previous"
          disabled={previousDisabled}
          onClick={() => onMonthChange(new Date(year, monthOfYear - 1, 1))}
        />
        <span aria-live="polite" className="heading-sm text-foreground">
          {monthLabelFormatter.format(month)}
        </span>
        <CalendarNavButton
          direction="next"
          disabled={nextDisabled}
          onClick={() => onMonthChange(new Date(year, monthOfYear + 1, 1))}
        />
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS.map((label) => (
          <span
            key={label}
            aria-hidden="true"
            className="flex size-8 items-center justify-center caption-md text-muted-foreground"
          >
            {label}
          </span>
        ))}
        {Array.from({ length: firstWeekday }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static leading placeholders
          <span key={`empty-${i}`} aria-hidden="true" className="size-8" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const date = new Date(year, monthOfYear, i + 1);
          const isDisabled =
            minSelectable !== null && date.getTime() < minSelectable.getTime();
          const position =
            props.mode === "range" ? rangePosition(date, props.value) : null;
          const isSelected =
            props.mode === "range"
              ? position === "start" || position === "end"
              : props.value != null && isSameDay(date, props.value);
          const isInRange = position === "middle";
          const isToday = isSameDay(date, today);

          return (
            <button
              key={date.getTime()}
              type="button"
              data-slot="calendar-day"
              data-range={position ?? undefined}
              aria-label={dayLabelFormatter.format(date)}
              aria-pressed={isSelected || isInRange}
              disabled={isDisabled}
              onClick={() => selectDay(date)}
              className={cn(
                "flex size-8 cursor-pointer items-center justify-center rounded-sm",
                "body-sm outline-none transition-colors",
                "disabled:pointer-events-none disabled:text-muted-foreground",
                isSelected
                  ? "bg-foreground text-background hover:bg-foreground/90"
                  : isInRange
                    ? "bg-secondary text-foreground hover:bg-(--cta-secondary-hover)"
                    : "text-foreground hover:bg-muted",
                isToday &&
                  !isSelected &&
                  "inset-ring inset-ring-muted-foreground",
                "focus-visible:ring-[3px] focus-visible:ring-ring/50",
              )}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { Calendar, type CalendarProps, type DateRange };
